<template>
  <div>
    <div class="modal fade show" tabindex="-1">
      <div class="modal-dialog dn-modal-dialog">
        <div class="modal-content dn-modal-content">
          <!-- Header -->
          <div class="dn-modal-header">
            <div class="dn-modal-heading">
              <h5 class="dn-modal-title">Add Template</h5>
              <p class="dn-modal-subtitle">Add the details</p>
            </div>
            <button
              type="button"
              class="dn-modal-close"
              aria-label="Close"
              @click="closeModal"
            >
              <i class="pi pi-times"></i>
            </button>
          </div>

          <form class="dn-form" @submit.prevent="addTemplate">
            <div class="dn-modal-body">
              <!-- Name -->
              <div class="dn-form-group">
                <label for="tpl-add-name">Name <span class="required-field">*</span></label>
                <input
                  id="tpl-add-name"
                  v-model="form.name"
                  type="text"
                  class="form-control"
                  :class="{ 'dn-input--error': errors.name }"
                  placeholder="e.g. Memorial plaque"
                  @input="clearError('name')"
                />
                <span v-if="errors.name" class="dn-error">{{ errors.name }}</span>
              </div>

              <!-- Fields: everything this template shows -->
              <div @focusin="customFieldsFocused = true" @focusout="customFieldsFocused = false">
                <TemplateCustomVariables
                  ref="customVariables"
                  v-model="form.customVariables"
                  v-model:content="form.content"
                  :show-all-errors="submitted"
                />
              </div>

              <!-- Content -->
              <div class="dn-form-group">
                <label>Content</label>
                <RichTextEditor
                  ref="editor"
                  v-model="form.content"
                  :variables="editorVariables"
                  :variable-action="newFieldAction"
                  height="320px"
                  @variable-action="addCustomVariable"
                />
                <p v-if="unknownTokens.length && !customFieldsFocused" class="dn-token-warning">
                  <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                  <span>
                    <template v-for="(token, index) in unknownTokens" :key="token">
                      <code>{{ '{' + token + '}' }}</code>{{ index < unknownTokens.length - 1 ? ', ' : ' ' }}
                    </template>
                    {{ unknownTokens.length === 1 ? "isn't a variable, so the screen shows it" : "aren't variables, so the screen shows them" }}
                    exactly as typed.
                  </span>
                </p>
              </div>
            </div>

            <div class="dn-modal-footer">
              <button type="button" class="btn btn-custom-secondary" @click="closeModal">
                Cancel
              </button>
              <button type="submit" class="btn btn-custom-primary" :disabled="templateIsLoading">
                <span
                  v-if="templateIsLoading"
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span v-else>Save</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RichTextEditor from '@/components/Rich_Text_Editor.vue';
import TemplateCustomVariables from '@/components/template/Template_Custom_Variables.vue';
import { REGISTER_TEMPLATE } from '@/store/action_types';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';
import {
  CUSTOM_VARIABLE_MAX_COUNT,
  donationTemplateMenuVariables,
  templateVariablesPayload,
  unknownTemplateTokens,
  validateTemplateVariables,
} from '@/utils/donation_template';

export default {
  name: 'Add_Template_Modal',
  components: {
    RichTextEditor,
    TemplateCustomVariables,
  },
  props: {
    selectedArtboard: {
      type: Object,
      required: true
    },
  },
  emits: ['closeAddTemplateModal', 'refreshTemplates'],
  data() {
    return {
      form: {
        name: '',
        content: '',
        customVariables: [],
      },
      errors: {},
      // A save was tried: every problem shows, not only those in fields left.
      submitted: false,
      customFieldsFocused: false,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    templateIsLoading() {
      return this.$store.getters.template.isLoading;
    },
    variableErrors() {
      return validateTemplateVariables(this.form.customVariables);
    },
    // The template's fields whose code is ready to place.
    editorVariables() {
      const notReady = Object.keys(this.variableErrors).filter((id) => this.variableErrors[id].code);
      return donationTemplateMenuVariables(this.form.customVariables, notReady);
    },
    newFieldAction() {
      return this.form.customVariables.length < CUSTOM_VARIABLE_MAX_COUNT
        ? { label: 'New field', icon: 'pi-plus' }
        : null;
    },
    unknownTokens() {
      return unknownTemplateTokens(this.form.content, this.form.customVariables);
    },
  },
  methods: {
    clearError(field) {
      if (this.errors[field]) {
        this.errors = { ...this.errors, [field]: undefined };
      }
    },
    addCustomVariable() {
      this.$refs.customVariables?.addVariable();
    },
    validate() {
      const errors = {};
      if (!this.form.name.trim()) errors.name = 'Name is required';

      this.errors = errors;
      return Object.keys(errors).length === 0 && Object.keys(this.variableErrors).length === 0;
    },
    // Brings the first problem into view, with the cursor in it.
    revealFirstError() {
      const field = this.$el.querySelector('.dn-input--error, .tcv-input--error, .tcv-code--error');
      if (!field) return;
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const input = field.matches('input') ? field : field.querySelector('input');
      input?.focus({ preventScroll: true });
    },
    closeModal() {
      this.$emit('closeAddTemplateModal');
    },
    async addTemplate() {
      this.submitted = true;
      // Any code changed but not yet carried into the content goes in now.
      this.$refs.customVariables?.commit();

      if (!this.validate()) {
        this.$toast.error('Please complete the required fields.', TOAST_GLOBAL_CONFIG);
        this.$nextTick(this.revealFirstError);
        return;
      }

      const payload = {
        name: this.form.name.trim(),
        content: this.form.content || '',
        customVariables: templateVariablesPayload(this.form.customVariables),
        screenId: this.$route.params.id ?? this.selectedArtboard.screenId,
        screenArtboardId: this.selectedArtboard.id,
        organizationId: this.account.organizationId,
      };

      const success = await this.$store.dispatch(REGISTER_TEMPLATE, payload);
      if (!success) return;

      this.$toast.success('Template saved successfully.', TOAST_GLOBAL_CONFIG);
      this.$emit('refreshTemplates');
      this.closeModal();
    },
  },
};
</script>

<style scoped>
/* ------------------------------ overlay ----------------------------- */
/* Lighter than a top-level modal: this one opens over the template list,
   which itself often sits over a donation form. */
.modal {
  display: none;
  background-color: rgba(17, 24, 39, 0.28);
  z-index: 1000;
}

.modal.show {
  display: block;
}

.dn-modal-dialog {
  max-width: 820px;
  margin: 5vh auto;
}

.dn-modal-content {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background-color: var(--color-bg-canvas);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

/* ------------------------------ header ------------------------------ */
.dn-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 1.4rem 1.6rem 0;
}

.dn-modal-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.dn-modal-subtitle {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.dn-modal-close {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}

.dn-modal-close:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

.dn-modal-close .pi {
  font-size: 0.85rem;
}

/* ------------------------------- body ------------------------------- */
.dn-modal-body {
  padding: 1.25rem 1.6rem 0.5rem;
  max-height: 74vh;
  overflow-y: auto;
}

.dn-form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  min-width: 0;
}

.dn-form-group label {
  margin-bottom: 6px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--color-text-primary) !important;
}

.dn-form-group .form-control {
  height: 44px !important;
  padding: 0 14px;
  font-size: 0.875rem;
  color: var(--color-text-primary);
  background-color: var(--color-bg-canvas);
}

.dn-form-group .form-control::placeholder {
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.dn-input--error.form-control {
  border-color: var(--color-danger) !important;
}

.dn-error {
  margin-top: 5px;
  font-size: 0.72rem;
  color: var(--color-danger);
}

/* Placeholders the content holds that no variable fills. */
.dn-token-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 8px 0 0;
  padding: 9px 12px;
  border: 1px solid #fde68a;
  border-radius: var(--radius-md);
  background: #fffbeb;
  font-size: 0.76rem;
  line-height: 1.45;
  color: #92400e;
}

.dn-token-warning .pi {
  margin-top: 2px;
  font-size: 0.74rem;
  color: var(--color-warning);
}

.dn-token-warning code {
  padding: 0 4px;
  border-radius: 4px;
  background: #fef3c7;
  color: inherit;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.72rem;
}

/* ------------------------------ footer ------------------------------ */
.dn-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 1rem 1.6rem 1.4rem;
  border-top: 1px solid var(--color-divider);
  margin-top: 6px;
}

.dn-modal-footer .btn {
  min-width: 96px;
  padding: 0.55rem 1.2rem;
  font-size: 0.875rem;
}

/* =========================================================================
   TABLET & MOBILE (≤ 991.98px)
   Header and footer stay pinned while only the body scrolls.
   ========================================================================= */
@media (max-width: 991.98px) {
  .dn-modal-dialog {
    max-width: 94%;
    margin: 3vh auto;
  }

  .dn-modal-content {
    max-height: 94dvh;
    display: flex;
    flex-direction: column;
  }

  .dn-form {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }

  .dn-modal-header,
  .dn-modal-footer {
    flex-shrink: 0;
  }

  .dn-modal-body {
    flex: 1 1 auto;
    max-height: none;
  }
}

@media (max-width: 768px) {
  .dn-modal-dialog {
    max-width: 96%;
    margin: 2.5vh auto;
  }

  .dn-modal-header {
    padding: 1.1rem 1.15rem 0;
  }

  .dn-modal-title {
    font-size: 1.15rem;
  }

  .dn-modal-body {
    padding: 1rem 1.15rem 0.5rem;
  }

  .dn-modal-footer {
    padding: 0.9rem 1.15rem 1.1rem;
  }

  /* Cancel / Save split the row evenly for easy thumb reach. */
  .dn-modal-footer .btn {
    flex: 1 1 0;
    min-width: 0;
  }
}
</style>
